// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@west/App/Components/Common/XngZone";
import React, { FC } from "react";

type Props = WidgetProps;

export interface SignInPageLayoutStyles {
    container?: InjectableCss;
    signInSection?: InjectableCss;
    createAccountSection?: InjectableCss;
    separator?: InjectableCss;
    upperSection?: InjectableCss;
    title?: TypographyPresentationProps;
}

export const signInPageLayout: SignInPageLayoutStyles = {};

const styles = signInPageLayout;
const SignInPageLayout: FC<Props> = ({ id }) => {
    const { allowGuestCheckout, pageType } = useAppSelector(state => {
        const { allowCreateAccount, allowGuestCheckout } = getSettingsCollection(state).accountSettings;
        const returnUrl = getReturnUrl(state);
        const shippingPageUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
        const referredFromShipping = returnUrl === shippingPageUrl;
        const onePageCheckoutPageUrl = getPageLinkByPageType(state, "OnePageCheckoutPage")?.url;
        const referredFromOnePageCheckout = returnUrl === onePageCheckoutPageUrl;
        return {
            pageType: getCurrentPage(state).type,
            allowGuestCheckout:
                allowGuestCheckout &&
                !getCurrentUserIsGuest(state) &&
                (referredFromShipping || referredFromOnePageCheckout),
        };
    });
    return (
        <>
            {allowGuestCheckout && (
                <Typography {...styles.title} forwardAs="h1" variant="h1">
                    {translate("Checkout")}
                </Typography>
            )}
            <StyledWrapper {...styles.container} data-test-selector="SignInPageLayout">
                <StyledWrapper {...styles.signInSection}>
                    <XngZone contentId={id} zoneName="Content00" hint="SignInExistingAccount" />
                </StyledWrapper>
                <StyledWrapper {...styles.separator} {...{ isSignInPage: pageType === "SignInPage" }}></StyledWrapper>
                <StyledWrapper {...styles.createAccountSection}>
                    <StyledWrapper {...styles.upperSection}>
                        <XngZone contentId={id} zoneName="Content01" hint="CreateAccount" />
                    </StyledWrapper>
                    <XngZone contentId={id} zoneName="Content02" hint="ExtraInfo" />
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: SignInPageLayout,
    definition: {
        group: "Layout" as any,
        displayName: "SignIn page layout",
        allowedContexts: ["SignInPage", "OrderStatusPage"],
    },
};

export default widgetModule;
